import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionStatusBadge } from '@/modules/subscriptions/components/SubscriptionStatusBadge';
import { EntitlementStatusBadge } from '@/modules/trials/components/EntitlementStatusBadge';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/utils/money';
import type { AdminPaymentItem } from '../types/adminPayment';
import { formatDateTime } from '../utils/paymentHelpers';
import { PaymentProviderBadge } from './PaymentProviderBadge';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import { PaymentTechnicalIdsSection } from './PaymentTechnicalIdsSection';

interface PaymentDetailDrawerProps {
  payment: AdminPaymentItem;
  onClose: () => void;
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <span className="text-xs font-medium text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm text-foreground break-words text-end sm:max-w-[65%]">
        {value?.trim() ? value : '—'}
      </span>
    </div>
  );
}

export function PaymentDetailDrawer({ payment, onClose }: PaymentDetailDrawerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    panelRef.current?.focus();
  }, [payment.payment_id]);

  return (
    <div className="fixed inset-0 z-50 flex" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        aria-label={t('common.close')}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-detail-title"
        className={cn(
          'relative ms-auto flex h-full w-full max-w-lg flex-col border-s border-border bg-background shadow-xl',
          'animate-in slide-in-from-end duration-200',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-4 sm:px-5">
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('payments.eyebrow')}
            </p>
            <h2
              id="payment-detail-title"
              className="text-lg font-semibold text-foreground truncate"
            >
              {t('payments.detail.title')}
            </h2>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <PaymentStatusBadge status={payment.status} />
              <PaymentProviderBadge provider={payment.provider} label={payment.provider_label} />
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label={t('common.close')}>
            <Icons.X className="h-5 w-5" />
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5 space-y-5">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              {t('payments.identity.sectionTitle')}
            </h3>
            <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
              <DetailRow label={t('payments.identity.company')} value={payment.tenant_name} />
              <DetailRow
                label={t('payments.identity.customerName')}
                value={payment.customer_name}
              />
              <DetailRow
                label={t('payments.identity.customerEmail')}
                value={payment.customer_email}
              />
              <DetailRow
                label={t('payments.identity.customerPhone')}
                value={payment.customer_phone}
              />
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              {t('payments.detail.paymentSection')}
            </h3>
            <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
              <DetailRow
                label={t('payments.amount')}
                value={formatMoney(payment.amount, payment.currency)}
              />
              <DetailRow
                label={t('payments.fee')}
                value={formatMoney(payment.fee_amount, payment.currency)}
              />
              <DetailRow
                label={t('payments.net')}
                value={formatMoney(payment.net_amount, payment.currency)}
              />
              <DetailRow label={t('payments.detail.kind')} value={payment.kind} />
              <DetailRow
                label={t('payments.detail.createdAt')}
                value={formatDateTime(payment.created_at, locale)}
              />
              <DetailRow
                label={t('payments.processedAt')}
                value={formatDateTime(payment.processed_at, locale)}
              />
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              {t('payments.detail.subscriptionSection')}
            </h3>
            <div className="rounded-lg border border-border bg-card p-3 space-y-3">
              <DetailRow label={t('payments.identity.product')} value={payment.product_name} />
              <DetailRow label={t('payments.identity.plan')} value={payment.plan_name} />
              <div className="flex flex-wrap items-center gap-2">
                <SubscriptionStatusBadge status={payment.subscription_status} />
                <EntitlementStatusBadge status={payment.entitlement_status} />
              </div>
            </div>
          </section>

          <PaymentTechnicalIdsSection payment={payment} />
        </div>
      </div>
    </div>
  );
}
